import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import Footer from '../../components/Footer';
import { Shield } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_mercamove/artifacts/ch8knfdf_Move_Logo_wHT.png" 
                alt="Mercapoli Move"
                className="h-8"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-700 hover:text-[#00BFA6]">
                  Iniciar Sesión
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary px-6 py-2 text-sm">
                  Registrarse
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-8 h-8 text-[#00BFA6]" />
              <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">
                Política de Privacidad
              </h1>
            </div>
            <p className="text-slate-500 mb-8">Última actualización: Enero 2025</p>

            <div className="prose prose-slate max-w-none">
              <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4">1. Introducción</h2>
              <p className="text-slate-600 mb-4">
                En Mercapoli Move, nos comprometemos a proteger su privacidad. Esta Política de 
                Privacidad describe cómo recopilamos, utilizamos, almacenamos y protegemos su 
                información personal cuando utiliza nuestra plataforma.
              </p>

              <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4">2. Información que Recopilamos</h2>
              <p className="text-slate-600 mb-4">
                Recopilamos la siguiente información:
              </p>
              <h3 className="text-lg font-semibold text-slate-700 mt-6 mb-3">2.1 Información de Registro</h3>
              <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
                <li>Nombre completo</li>
                <li>Dirección de correo electrónico</li>
                <li>Número de teléfono</li>
                <li>País de residencia</li>
                <li>Fotografía de perfil (opcional)</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-slate-700 mt-6 mb-3">2.2 Información de Uso</h3>
              <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
                <li>Ubicación en tiempo real (con su consentimiento)</li>
                <li>Historial de viajes y transacciones</li>
                <li>Interacciones con la plataforma</li>
                <li>Información del dispositivo</li>
              </ul>

              <h3 className="text-lg font-semibold text-slate-700 mt-6 mb-3">2.3 Información de Pago</h3>
              <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
                <li>Datos de tarjetas de crédito/débito (procesados por proveedores seguros)</li>
                <li>Información de Digi Wallet</li>
                <li>Historial de transacciones</li>
              </ul>

              <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4">3. Uso de la Información</h2>
              <p className="text-slate-600 mb-4">
                Utilizamos su información para:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
                <li>Proporcionar y mejorar nuestros servicios</li>
                <li>Procesar pagos y transacciones</li>
                <li>Comunicarnos con usted sobre su cuenta y servicios</li>
                <li>Garantizar la seguridad de la plataforma</li>
                <li>Cumplir con obligaciones legales</li>
                <li>Personalizar su experiencia</li>
              </ul>

              <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4">4. Compartición de Información</h2>
              <p className="text-slate-600 mb-4">
                Compartimos su información con:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
                <li><strong>Conductores y usuarios:</strong> Información necesaria para completar el servicio</li>
                <li><strong>Proveedores de servicios:</strong> Procesadores de pago, servicios de verificación</li>
                <li><strong>Autoridades:</strong> Cuando lo exija la ley o para proteger derechos</li>
              </ul>

              <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4">5. Seguridad de Datos</h2>
              <p className="text-slate-600 mb-4">
                Implementamos medidas de seguridad técnicas y organizativas para proteger su 
                información, incluyendo:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
                <li>Encriptación de datos en tránsito y en reposo</li>
                <li>Controles de acceso estrictos</li>
                <li>Monitoreo continuo de seguridad</li>
                <li>Auditorías periódicas</li>
              </ul>

              <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4">6. Sus Derechos</h2>
              <p className="text-slate-600 mb-4">
                Usted tiene derecho a:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
                <li>Acceder a sus datos personales</li>
                <li>Rectificar información incorrecta</li>
                <li>Solicitar la eliminación de sus datos</li>
                <li>Oponerse al procesamiento de sus datos</li>
                <li>Portabilidad de datos</li>
                <li>Retirar su consentimiento en cualquier momento</li>
              </ul>

              <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4">7. Retención de Datos</h2>
              <p className="text-slate-600 mb-4">
                Conservamos sus datos personales durante el tiempo necesario para cumplir con los 
                fines descritos en esta política, a menos que la ley exija un período de retención 
                más largo.
              </p>

              <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4">8. Menores de Edad</h2>
              <p className="text-slate-600 mb-4">
                Nuestros servicios no están dirigidos a menores de 18 años. No recopilamos 
                intencionalmente información de menores de edad.
              </p>

              <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4">9. Cambios a esta Política</h2>
              <p className="text-slate-600 mb-4">
                Podemos actualizar esta Política de Privacidad periódicamente. Le notificaremos 
                cualquier cambio material a través de la plataforma o por correo electrónico.
              </p>

              <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4">10. Contacto</h2>
              <p className="text-slate-600 mb-4">
                Para ejercer sus derechos o si tiene preguntas sobre esta política, contáctenos:
              </p>
              <p className="text-slate-600">
                <strong>Email:</strong> privacidad@mercapolimove.com<br />
                <strong>Delegado de Protección de Datos:</strong> dpo@mercapolimove.com
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
